/**
 * TomTom Traffic API Service
 * Provides real-time traffic flow, incidents, and road news
 */

import { Alert, HazardType, LatLng } from "../types";

// API Configuration
const TOMTOM_API_KEY = import.meta.env.VITE_TOMTOM_API_KEY || '';
const BASE_URL = 'https://api.tomtom.com';

// TomTom Traffic Flow Tiles URL (for Leaflet layer)
export const getTrafficFlowTileUrl = (): string => {
    return `https://api.tomtom.com/traffic/map/4/tile/flow/relative0/{z}/{x}/{y}.png?key=${TOMTOM_API_KEY}&thickness=10`;
};

// TomTom Traffic Incidents Tiles URL
export const getTrafficIncidentTileUrl = (): string => {
    return `https://api.tomtom.com/traffic/map/4/tile/incidents/s1/{z}/{x}/{y}.png?key=${TOMTOM_API_KEY}`;
};

// TomTom Base Map Vector Tiles URL
export const getTomTomBaseTileUrl = (style: 'SATELLITE' | 'DARK' | 'STREET' = 'DARK'): string | null => {
    if (style === 'SATELLITE') return null; // Use Esri/Satellite fallback

    // Style: basic_night vs basic_main
    const variant = style === 'STREET' ? 'basic/main' : 'basic/night';
    return `https://api.tomtom.com/map/1/tile/${variant}/{z}/{x}/{y}.png?key=${TOMTOM_API_KEY}`;
};

// TomTom Incident Categories Mapping
const INCIDENT_CATEGORY_MAP: Record<number, HazardType> = {
    0: HazardType.TRAFFIC,       // Unknown
    1: HazardType.TRAFFIC,       // Accident
    2: HazardType.TRAFFIC,       // Fog
    3: HazardType.TRAFFIC,       // Dangerous Conditions
    4: HazardType.WEATHER,       // Rain
    5: HazardType.WEATHER,       // Ice
    6: HazardType.TRAFFIC,       // Jam
    7: HazardType.TRAFFIC,       // Lane Closed
    8: HazardType.TRAFFIC,       // Road Closed
    9: HazardType.TRAFFIC,       // Road Works
    10: HazardType.WEATHER,      // Wind
    11: HazardType.TRAFFIC,      // Flooding
    14: HazardType.TRAFFIC,      // Broken Down Vehicle
};

// Severity mapping based on delay magnitude
const getIncidentSeverity = (delayMagnitude: number): 'low' | 'medium' | 'high' | 'critical' => {
    if (delayMagnitude >= 4) return 'critical';
    if (delayMagnitude >= 3) return 'high';
    if (delayMagnitude >= 2) return 'medium';
    return 'low';
};

// TomTom API Response Types
interface TomTomIncident {
    id: string;
    type: string;
    geometry: {
        type: string;
        coordinates: number[] | number[][];
    };
    properties: {
        iconCategory: number;
        magnitudeOfDelay: number;
        events: Array<{
            description: string;
            code: number;
            iconCategory: number;
        }>;
        startTime?: string;
        endTime?: string;
        from?: string;
        to?: string;
        length?: number;
        delay?: number;
        roadNumbers?: string[];
    };
}

interface TomTomIncidentResponse {
    incidents: TomTomIncident[];
}

/**
 * Fetch traffic incidents from TomTom API within a bounding box
 */
export const fetchTomTomIncidents = async (
    center: LatLng,
    radiusKm: number = 25
): Promise<Alert[]> => {
    if (!TOMTOM_API_KEY) {
        console.warn('TomTom API key not configured');
        return [];
    }

    // Calculate bounding box from center and radius
    const latOffset = radiusKm / 111; // ~111km per degree latitude
    const lngOffset = radiusKm / (111 * Math.cos(center.lat * Math.PI / 180));

    const minLat = center.lat - latOffset;
    const maxLat = center.lat + latOffset;
    const minLng = center.lng - lngOffset;
    const maxLng = center.lng + lngOffset;

    const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;

    // TomTom Incident Details API v5
    const url = `${BASE_URL}/traffic/services/5/incidentDetails?key=${TOMTOM_API_KEY}&bbox=${bbox}&fields={incidents{type,geometry{type,coordinates},properties{iconCategory,magnitudeOfDelay,events{description,code,iconCategory},startTime,endTime,from,to,length,delay,roadNumbers}}}&language=en-US&categoryFilter=0,1,2,3,4,5,6,7,8,9,10,11,14&timeValidityFilter=present`;

    try {
        console.log('📡 Fetching TomTom incidents...');
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`TomTom API error: ${response.status}`);
        }

        const data: TomTomIncidentResponse = await response.json();

        if (!data.incidents || data.incidents.length === 0) {
            console.log('ℹ️ No TomTom incidents found in area');
            return [];
        }

        console.log(`✅ TomTom returned ${data.incidents.length} incidents`);

        // Convert TomTom incidents to Protectly Alert format
        const alerts: Alert[] = data.incidents.map((incident, index) => {
            // Get coordinates (handle both Point and LineString)
            let coords: LatLng;
            if (incident.geometry.type === 'Point') {
                const [lng, lat] = incident.geometry.coordinates as number[];
                coords = { lat, lng };
            } else if (incident.geometry.type === 'LineString') {
                // Use first point of LineString
                const firstPoint = (incident.geometry.coordinates as number[][])[0];
                coords = { lat: firstPoint[1], lng: firstPoint[0] };
            } else {
                coords = center; // Fallback
            }

            const props = incident.properties;
            const hazardType = INCIDENT_CATEGORY_MAP[props.iconCategory] || HazardType.TRAFFIC;
            const severity = getIncidentSeverity(props.magnitudeOfDelay);

            // Build description
            const eventDescriptions = props.events?.map(e => e.description).join(', ') || 'Traffic incident';
            const roadInfo = props.roadNumbers?.join(', ') || props.from || 'Unknown road';
            const delayMinutes = props.delay ? Math.round(props.delay / 60) : 0;

            return {
                id: `tomtom-${incident.id || index}-${Date.now()}`,
                type: hazardType,
                severity,
                location: roadInfo,
                coordinates: coords,
                timestamp: props.startTime || new Date().toISOString(),
                time: props.startTime ? new Date(props.startTime).toLocaleTimeString() : 'Live',
                distance: props.length ? `${(props.length / 1000).toFixed(1)}km` : 'N/A',
                isOfficial: true,
                source: 'TomTom Traffic',
                truthScore: 98,
                description: `${eventDescriptions}${delayMinutes > 0 ? `. Expected delay: ${delayMinutes} min` : ''}`,
                advice: generateTrafficAdvice(props.iconCategory, delayMinutes),
                imageUrl: getIncidentImageUrl(props.iconCategory),
                isRedAlert: severity === 'critical',
            };
        });

        return alerts;
    } catch (error) {
        console.error('❌ TomTom API Error:', error);
        return [];
    }
};

/**
 * Generate traffic advice based on incident type
 */
const generateTrafficAdvice = (iconCategory: number, delayMinutes: number): string[] => {
    const advice: string[] = [];

    if (delayMinutes > 15) {
        advice.push('Consider using alternative routes');
        advice.push(`Expected delay: ${delayMinutes} minutes`);
    } else if (delayMinutes > 5) {
        advice.push('Minor delays expected');
    }

    switch (iconCategory) {
        case 1: // Accident
            advice.push('Emergency services may be present');
            advice.push('Reduce speed and drive cautiously');
            break;
        case 6: // Jam
            advice.push('Heavy traffic congestion ahead');
            advice.push('Use navigation app for live updates');
            break;
        case 8: // Road Closed
            advice.push('Road is completely closed');
            advice.push('Seek alternative route immediately');
            break;
        case 9: // Road Works
            advice.push('Construction zone - reduce speed');
            advice.push('Follow temporary road signs');
            break;
        case 4: // Rain
        case 5: // Ice
        case 10: // Wind
            advice.push('Weather affecting road conditions');
            advice.push('Increase following distance');
            break;
    }

    return advice.length > 0 ? advice : ['Drive safely and stay alert'];
};

/**
 * Get appropriate image URL for incident type
 */
const getIncidentImageUrl = (iconCategory: number): string => {
    // Use Unsplash for incident imagery
    const imageMap: Record<number, string> = {
        1: 'https://images.unsplash.com/photo-1581092160562-40aa08e78669?w=400', // Accident
        6: 'https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=400', // Traffic Jam
        8: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',   // Road Closed
        9: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400', // Road Works
        4: 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=400', // Rain
    };

    return imageMap[iconCategory] || 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400';
};

/**
 * Get traffic flow data for a specific location
 */
export const fetchTrafficFlow = async (center: LatLng): Promise<{
    currentSpeed: number;
    freeFlowSpeed: number;
    congestionLevel: 'free' | 'light' | 'moderate' | 'heavy' | 'standstill';
} | null> => {
    if (!TOMTOM_API_KEY) return null;

    const url = `${BASE_URL}/traffic/services/4/flowSegmentData/absolute/10/json?key=${TOMTOM_API_KEY}&point=${center.lat},${center.lng}`;

    try {
        const response = await fetch(url);
        if (!response.ok) return null;

        const data = await response.json();
        const flow = data.flowSegmentData;

        if (!flow) return null;

        const ratio = flow.currentSpeed / flow.freeFlowSpeed;
        let congestionLevel: 'free' | 'light' | 'moderate' | 'heavy' | 'standstill';

        if (ratio >= 0.9) congestionLevel = 'free';
        else if (ratio >= 0.7) congestionLevel = 'light';
        else if (ratio >= 0.5) congestionLevel = 'moderate';
        else if (ratio >= 0.2) congestionLevel = 'heavy';
        else congestionLevel = 'standstill';

        return {
            currentSpeed: flow.currentSpeed,
            freeFlowSpeed: flow.freeFlowSpeed,
            congestionLevel
        };
    } catch (error) {
        console.error('Traffic flow fetch error:', error);
        return null;
    }
};

/**
 * Emergency POI Search
 */
export interface PoiResult {
    id: string;
    name: string;
    type: 'HOSPITAL' | 'POLICE' | 'FIRE_STATION' | 'PHARMACY';
    coordinates: LatLng;
    address: string;
    phone?: string;
    distance?: number; // meters
}

export const searchNearbyEmergencyPOIs = async (center: LatLng, radiusKm: number = 10): Promise<PoiResult[]> => {
    if (!TOMTOM_API_KEY) return [];

    // Categories: 7321 (Hospital), 7322 (Police Station), 7392 (Fire Station), 7326 (Pharmacy)
    const categorySet = "7321,7322,7392,7326";
    const lat = center.lat;
    const lon = center.lng;
    const radiusMeters = radiusKm * 1000;

    const url = `${BASE_URL}/search/2/poiSearch/emergency.json?key=${TOMTOM_API_KEY}&lat=${lat}&lon=${lon}&radius=${radiusMeters}&categorySet=${categorySet}&limit=50&language=en-US`;

    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("POI Search Failed");
        const data = await response.json();

        if (!data.results) return [];

        return data.results.map((res: any) => {
            let type: PoiResult['type'] = 'HOSPITAL';
            const catId = res.poi.categorySet[0]?.id;

            if (catId === 7321) type = 'HOSPITAL';
            else if (catId === 7322) type = 'POLICE';
            else if (catId === 7392) type = 'FIRE_STATION';
            else if (catId === 7326) type = 'PHARMACY';
            else if (res.poi.name.toLowerCase().includes('police')) type = 'POLICE'; // Fallback
            else if (res.poi.name.toLowerCase().includes('fire')) type = 'FIRE_STATION';
            else if (res.poi.name.toLowerCase().includes('pharmacy')) type = 'PHARMACY';

            return {
                id: res.id,
                name: res.poi.name,
                type,
                coordinates: { lat: res.position.lat, lng: res.position.lon },
                address: res.address.freeformAddress,
                phone: res.poi.phone,
                distance: res.dist
            };
        });

    } catch (e) {
        console.warn("TomTom POI Search Error:", e);
        return [];
    }
};

export default {
    getTrafficFlowTileUrl,
    getTrafficIncidentTileUrl,
    getTomTomBaseTileUrl,
    fetchTomTomIncidents,
    fetchTrafficFlow,
    searchNearbyEmergencyPOIs
};
